import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import PageHeader from '@/components/PageHeader';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { RunningClub } from '@/models/running-club.model';
import { getRunningClub } from '@/services/running-club.service';
import RunningClubForm from './running-club-form';
import LinkList from '@/pages/links/link-list';
import RunningClubEventListPage from '../running-club-event/list/running-club-event-list';
import RunningClubMemberList from '../running-club-member/running-club-member-list';
import RunningClubMediaList from '../running-club-media/running-club-media-list';
import RunningClubAlbumList from '../running-club-album/running-club-album-list';
import RunningClubAlbumImages from '../running-club-album/running-club-album-images';
import RunningClubChannelList from '../running-club-channel/running-club-channel-list';
import RunningClubChannelMessages from '../running-club-channel/running-club-channel-messages';
import { RunningClubAlbum } from '@/models/running-club-album.model';
import { Channel } from '@/models/channel.model';
import RunningClubActions from './running-club-actions';
import RunningClubCaptainProfileList from '../running-club-captain-profile/running-club-captain-profile-list';
import RunningClubPointAccountList from '../running-club-point-account/running-club-point-account-list';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect}>
      <Nav.Item eventKey="running-club-form">Detail</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="running-club-links">Links</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="running-club-events">Events</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="running-club-members">Members</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="running-club-actions">Action</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="running-club-medias">Medias</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="running-club-albums">Albums</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="running-club-channels">Channels</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="running-club-captain-profiles">Captain Profiles</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="running-club-point-accounts">Point Accounts</Nav.Item>
    </Nav>
  );
};

const Page = () => {

  const requestedTab = useLocation()?.state?.tab;

  const [activeTab, setActiveTab] = useState('running-club-form');
  const [formObject, setFormObject] = useState<RunningClub|undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState<RunningClubAlbum|undefined>(undefined);
  const [selectedChannel, setSelectedChannel] = useState<Channel|undefined>(undefined);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;


  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<RunningClub> = await getRunningClub(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching Running Club: ${errorModel?.errorMessage}`);
        nav('running-clubs');
      }
      finally {
        setLoading(false);
      }
    }
  };
 
  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit Club: ${formObject.name}` : 'New Club'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='running-clubs'> Running Clubs</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" active={activeTab} onSelect={key => setActiveTab(key)} newMode={pageState == PageState.New} />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'running-club-form' && <RunningClubForm formObject={formObject} pageState={pageState}/>}
          {activeTab == 'running-club-links' && id && <LinkList runningClubId={formObject?.id}/>}
          {activeTab == 'running-club-events' && id && <RunningClubEventListPage runningClubId={formObject?.id}/>}
          {activeTab == 'running-club-members' && id && <RunningClubMemberList runningClubId={formObject?.id}/>}
          {activeTab == 'running-club-actions' && id && formObject && <RunningClubActions formObject={formObject} fetchData={fetchData}/>}
          {activeTab == 'running-club-medias' && id && <RunningClubMediaList runningClubId={formObject?.id}/>}
          {activeTab == 'running-club-albums' && id && (
            selectedAlbum ? (
              <RunningClubAlbumImages
                albumId={selectedAlbum.id}
                albumName={selectedAlbum.name}
                onBack={() => setSelectedAlbum(undefined)}
              />
            ) : (
              <RunningClubAlbumList
                runningClubId={formObject?.id}
                onSelectAlbum={setSelectedAlbum}
              />
            )
          )}
          {activeTab == 'running-club-captain-profiles' && id && <RunningClubCaptainProfileList runningClubId={formObject?.id}/>}
          {activeTab == 'running-club-point-accounts' && id && <RunningClubPointAccountList runningClubId={formObject?.id}/>}
          {activeTab == 'running-club-channels' && id && (
            selectedChannel ? (
              <RunningClubChannelMessages
                channelId={selectedChannel.id}
                channelName={selectedChannel.name}
                onBack={() => setSelectedChannel(undefined)}
              />
            ) : (
              <RunningClubChannelList
                runningClubId={formObject?.id}
                onSelectChannel={setSelectedChannel}
              />
            )
          )}
        </>
      }
    </Panel>
  );
};

export default Page;
